import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Menu, X, Key } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Sidebar } from './Sidebar';
import { Button } from '@/components/ui/button';
import { CreateProjectModal } from '@/components/projects';
import { useProjects } from '@/hooks/useProjects';
import { usePlanLimits } from '@/hooks/usePlanLimits';

export function AppShell() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const navigate = useNavigate();
  const { createProject, isCreatingProject, createProjectError, selectProject, primaryTeamId } = useProjects();
  const { canCreateProject, refetchUsage } = usePlanLimits(primaryTeamId);

  const isAtProjectLimit = !canCreateProject();

  const handleOpenCreateModal = () => {
    if (isAtProjectLimit) return;
    setIsCreateModalOpen(true);
  };

  const handleCreateProjectSubmit = (data: { name: string; description?: string }) => {
    createProject(data, {
      onSuccess: (newProject) => {
        setIsCreateModalOpen(false);
        selectProject(newProject.id);
        refetchUsage();
        navigate(`/projects/${newProject.id}`);
      },
    });
  };

  return (
    <div className="h-screen bg-background flex relative overflow-hidden">
      {/* Background decorations */}
      <div className="fixed inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full opacity-[0.03]"
          style={{
            background: 'radial-gradient(circle, hsl(var(--primary)) 0%, transparent 70%)',
            filter: 'blur(100px)',
          }}
        />
        <div
          className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full opacity-[0.02]"
          style={{
            background: 'radial-gradient(circle, hsl(var(--primary)) 0%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
      </div>

      {/* Left: Sidebar (scrolls independently) */}
      <div className="hidden lg:block flex-shrink-0 relative z-10">
        <Sidebar onCreateProject={handleOpenCreateModal} isCreateProjectDisabled={isAtProjectLimit} />
      </div>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <div className="fixed inset-0 bg-background/80 backdrop-blur-md" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile sidebar */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-y-0 left-0 z-50 lg:hidden"
          >
            <Sidebar onCreateProject={handleOpenCreateModal} isCreateProjectDisabled={isAtProjectLimit} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Right: Page content (scrolls independently) */}
      <div className="flex-1 flex flex-col min-w-0 relative z-10">
        {/* Mobile header */}
        <header className="h-12 bg-card/50 backdrop-blur-md border-b border-border/50 flex items-center justify-between px-4 lg:hidden flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="h-8 w-8 hover:bg-muted/50"
          >
            {isMobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </Button>
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 bg-gradient-to-br from-primary to-primary/70 rounded-md flex items-center justify-center shadow-lg shadow-primary/20">
              <Key className="h-3.5 w-3.5 text-primary-foreground" />
            </div>
            <span className="text-sm font-semibold tracking-tight">KeyEnv</span>
          </div>
          <div className="w-8" />
        </header>

        {/* Page content - scrollable */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>

      <CreateProjectModal 
        isOpen={isCreateModalOpen} 
        onClose={() => setIsCreateModalOpen(false)} 
        onSubmit={handleCreateProjectSubmit} 
        isLoading={isCreatingProject} 
        error={createProjectError} 
      />
    </div>
  );
}
